import { safeFetchAPI } from '@/api';
import { log, logger, utils } from '@/helpers';
import { TopConvertedMethod } from '@/pages/campaign-reports/campaign.report.utils';
import {
	CampaignReportFilters,
	ListCampaignStatsByCampaignRequest,
	PaginatedData,
	PaginationReq,
	TopConversionsByBrand,
	TopConversionsByLocation,
	TopConversionsByProduct,
} from '@/types';
import { create, StoreApi, UseBoundStore } from 'zustand';
import { getCampaignReportFiltersStore, LOCAL_CAMPAIGN_REPORT_TEST_TOKEN, LOCAL_CAMPAIGN_REPORT_TEST_UID } from './createCampaignReportFiltersStore';
import { cloneDeep } from 'lodash';

export type TopConvertingReportEntry = TopConversionsByLocation | TopConversionsByBrand | TopConversionsByProduct;

type DataType =
	| {
			data: TopConversionsByLocation[];
			type: TopConvertedMethod.LOCATIONS;
	  }
	| {
			data: TopConversionsByBrand[];
			type: TopConvertedMethod.BRANDS;
	  }
	| {
			data: TopConversionsByProduct[];
			type: TopConvertedMethod.PRODUCTS;
	  };

export type TopConvertingReportStoreState = {
	loaded: boolean;
	loading: boolean;
	campaignID: string;
	variantID: string | undefined;
} & DataType &
	PaginationReq & {
		totalResult: number;
	};

export type TopConvertingReportStoreActions = {
	fetchTopConvertingReport: (req?: Partial<ListCampaignStatsByCampaignRequest>) => Promise<void>;
	nextPage: () => void;
	prevPage: () => void;
	reset: (filters?: CampaignReportFilters, type?: TopConvertedMethod) => void;
};

export type TopConvertingReportStore = TopConvertingReportStoreState & TopConvertingReportStoreActions;

const initialState: TopConvertingReportStoreState = {
	loaded: false,
	loading: false,
	type: TopConvertedMethod.PRODUCTS,
	data: [],
	page: 1,
	limit: 10,
	totalResult: 0,
	campaignID: 'global',
	variantID: undefined,
};

export const createCampaignReportTopConvertingStore = (campaignID = 'global', variantID?: string, retailerCampaignID?: string) =>
	create<TopConvertingReportStore>((set, get) => ({
		...initialState,
		campaignID,
		variantID,
		fetchTopConvertingReport: async (req?: Partial<ListCampaignStatsByCampaignRequest>) => {
			const { type } = get();
			const pagination = req?.pagination ?? { page: get().page, limit: get().limit };
			const { page, limit } = pagination;

			set({ loading: true });

			const payload = {
				pagination,
				...req,
			};

			// Payload & response are the same, but the endpoint is different
			const endpoint = retailerCampaignID ? `/co-marketing/reports/conversions/:uid/${retailerCampaignID}/top/${type}` : `/conversions/:uid/reports/top/${type}`;

			const [response, error] = await safeFetchAPI<PaginatedData<TopConvertingReportEntry>>(endpoint, {
				method: 'POST',
				payload,
				forceToken: LOCAL_CAMPAIGN_REPORT_TEST_TOKEN,
				uid: LOCAL_CAMPAIGN_REPORT_TEST_UID,
			});

			if (error) {
				utils?.toaster?.addToast?.({
					message: error || 'Error loading top converting report',
					type: 'danger',
				});
				return;
			}

			logger.log(endpoint).prefix(campaignID).add({ campaignID, variantID, retailerCampaignID }).addnl('response', response);

			const totalResult = response?.pagination?.totalResult ?? 0;

			set({
				loaded: true,
				loading: false,
				page,
				limit,
				data: cloneDeep(response?.data ?? []),
				totalResult,
			});
		},
		nextPage: () => {
			const campaignID = get().campaignID;
			const storeState = getCampaignReportFiltersStore(campaignID).getState();
			const filters = storeState.getReportFiltersFromRawFilters(campaignID, get().variantID);
			get().fetchTopConvertingReport({
				...filters,
				pagination: { page: get().page + 1, limit: get().limit },
			});
		},
		prevPage: () => {
			const campaignID = get().campaignID;
			const storeState = getCampaignReportFiltersStore(campaignID).getState();
			const filters = storeState.getReportFiltersFromRawFilters(campaignID, get().variantID);
			get().fetchTopConvertingReport({
				...filters,
				pagination: { page: get().page - 1, limit: get().limit },
			});
		},
		reset: (filters?: CampaignReportFilters, type?: TopConvertedMethod) => {
			set({ ...initialState, type: type ?? initialState.type ?? TopConvertedMethod.PRODUCTS } as TopConvertingReportStoreState);
			if (filters) {
				get().fetchTopConvertingReport({ ...filters });
			}
		},
	}));

const storeInstances = new Map<string, UseBoundStore<StoreApi<TopConvertingReportStore>>>();

export const getCampaignReportTopConvertingStore = (id = 'global', variantID?: string, retailerCampaignID?: string) => {
	const key = `${id}-${variantID}-${retailerCampaignID}`;
	if (!storeInstances.has(key)) {
		storeInstances.set(key, createCampaignReportTopConvertingStore(id, variantID, retailerCampaignID));
	}
	return storeInstances.get(key)!;
};
